import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatError } from "./api";
import { ensurePushIdentity, pushLogout } from "./pushNotifications";

const AuthCtx = createContext(null);

const VIEW_MODE_KEY = "dv:admin_view_mode";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = unauth, object = auth
  const [loading, setLoading] = useState(true);
  // Admin-only display override. null = use real role. Persisted per browser.
  const [viewMode, setViewModeState] = useState(() => {
    try {
      const stored = localStorage.getItem(VIEW_MODE_KEY);
      return stored && ["constructor", "marketing", "investor"].includes(stored)
        ? stored
        : null;
    } catch { return null; }
  });

  const setViewMode = useCallback((mode) => {
    // Only admins may switch views; non-admins silently noop.
    if (user && user.role !== "admin") return;
    // Normalize "admin" / falsy back to null (clears the override).
    const next = (mode && mode !== "admin"
      && ["constructor", "marketing", "investor"].includes(mode))
      ? mode : null;
    setViewModeState(next);
    try {
      if (next) localStorage.setItem(VIEW_MODE_KEY, next);
      else localStorage.removeItem(VIEW_MODE_KEY);
    } catch { /* ignore */ }
  }, [user]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // PUSH Fase 1A: con sesión conocida, ata la identidad del dispositivo
  // OneSignal a nuestro user_id (external_id). Idempotente, lazy (solo
  // si el backend dice enabled) y best-effort: jamás pide permiso por
  // sí mismo y jamás rompe la autenticación.
  useEffect(() => {
    if (user && user.user_id) {
      ensurePushIdentity(user.user_id);
    }
  }, [user]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const register = async (email, password, name, role = "investor") => {
    const { data } = await api.post("/auth/register", { email, password, name, role });
    setUser(data);
    return data;
  };

  const logout = async () => {
    pushLogout(); // desliga el dispositivo de la identidad; best-effort
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
    setViewModeState(null);
    try { localStorage.removeItem(VIEW_MODE_KEY); } catch { /* ignore */ }
  };

  const refreshMe = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {}
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, refreshMe, setUser, viewMode, setViewMode }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
export { formatError };
